import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileText, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import { exportPDF, exportExcel, brl } from "@/utils/reportExporter";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers genéricos
// ─────────────────────────────────────────────────────────────────────────────
const fmtBR = (iso) => {
  if (!iso) return "";
  try {
    const s = String(iso);
    if (s.length === 10) return new Date(s + "T12:00:00").toLocaleDateString("pt-BR");
    return new Date(s).toLocaleString("pt-BR");
  } catch { return iso; }
};

function FormatPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <button type="button" onClick={()=>onChange("pdf")} className={`p-3 rounded-md border text-sm ${value==="pdf" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-neutral-400"}`} data-testid="format-pdf">
        <FileText size={16} className="inline mr-1.5"/> PDF
      </button>
      <button type="button" onClick={()=>onChange("xlsx")} className={`p-3 rounded-md border text-sm ${value==="xlsx" ? "bg-emerald-600 text-white border-emerald-600" : "border-[#E5E5E5] hover:border-neutral-400"}`} data-testid="format-xlsx">
        <FileSpreadsheet size={16} className="inline mr-1.5"/> Excel (.xlsx)
      </button>
    </div>
  );
}

function CategoriesChips({ categories, selected, onToggle, onSelectAll, onClear }) {
  if (!categories?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-xs">
        <button type="button" onClick={onSelectAll} className="text-emerald-600 hover:underline" data-testid="cat-select-all">Todas</button>
        <span className="text-neutral-300">·</span>
        <button type="button" onClick={onClear} className="text-neutral-500 hover:underline">Limpar</button>
        <span className="text-neutral-400 ml-auto">{selected.length}/{categories.length}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
        {categories.map(c => {
          const on = selected.includes(c.id);
          return (
            <button key={c.id} type="button" onClick={()=>onToggle(c.id)}
              className={`text-[11px] px-2 py-1 rounded-full border ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white dark:bg-transparent border-[#E5E5E5] text-neutral-700 dark:text-neutral-300"}`}
              data-testid={`cat-chip-${c.id}`}>{c.name}</button>
          );
        })}
      </div>
    </div>
  );
}

// Helper: abre uma janela popup IMEDIATAMENTE (síncrono, dentro do click
// handler) com um placeholder "Gerando relatório…". Indispensável pra evitar
// bloqueio do navegador, que só permite window.open dentro de um user gesture.
function openLoaderPopup() {
  const w = window.open("", "_blank");
  if (!w) return null;
  try {
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Gerando relatório…</title>
<style>html,body{height:100%;margin:0;font-family:-apple-system,sans-serif;background:#fafafa;color:#111;display:flex;align-items:center;justify-content:center}
.box{text-align:center}.sp{width:34px;height:34px;border-radius:50%;border:3px solid #ddd;border-top-color:#10B981;animation:s 0.9s linear infinite;margin:0 auto 12px}
@keyframes s{to{transform:rotate(360deg)}}</style></head><body><div class="box"><div class="sp"></div><div>Gerando relatório…</div></div></body></html>`);
    w.document.close();
  } catch {}
  return w;
}

// ─────────────────────────────────────────────────────────────────────────────
async function getStoreForHeader() {
  try {
    const r = await api.get("/store");
    return r.data || {};
  } catch { return {}; }
}

// ═════════════════════════════════════════════════════════════════════════════
// 1) RELATÓRIO DE PRODUTOS (Estoque)
// ═════════════════════════════════════════════════════════════════════════════
export function ProductsExportDialog({ open, onClose, products, categories, suppliers }) {
  const [format, setFormat] = useState("pdf");
  const [stockMode, setStockMode] = useState("with"); // with|without
  const [allCats, setAllCats] = useState(true);
  const [selCats, setSelCats] = useState([]);
  const [showSupplier, setShowSupplier] = useState(false);
  const [showCosts, setShowCosts] = useState(true);
  const [showProfit, setShowProfit] = useState(true);

  useEffect(() => {
    if (!open) return;
    setFormat("pdf"); setStockMode("with");
    setAllCats(true); setSelCats([]);
    setShowSupplier(false); setShowCosts(true); setShowProfit(true);
  }, [open]);

  const generate = async () => {
    // CRÍTICO: window.open precisa rodar SÍNCRONO no click. Por isso abrimos
    // a popup ANTES de qualquer await. Se format=xlsx, fechamos depois.
    const popup = format === "pdf" ? openLoaderPopup() : null;
    if (format === "pdf" && !popup) {
      toast.error("Permita popups para exportar o PDF.");
      return;
    }
    const closeOnError = () => { try { popup && popup.close(); } catch {} };
    const store = await getStoreForHeader();
    const catMap = Object.fromEntries((categories || []).map(c => [c.id, c]));
    const supMap = Object.fromEntries((suppliers || []).map(s => [s.id, s]));
    const cats = allCats ? null : new Set(selCats);
    // "Explode" produtos: cada variação vira uma linha. Produtos sem variações
    // viram 1 linha. Quantidade é por variação.
    const rows = [];
    (products || []).forEach(p => {
      if (cats && !cats.has(p.category_id)) return;
      const cat = catMap[p.category_id]?.name || "—";
      const sup = supMap[p.supplier_id]?.name || "";
      const variants = (p.variants && p.variants.length) ? p.variants : null;
      const cost = Number(p.cost || 0);
      const price = Number(p.price || 0);
      // Valor vendido = promo_price se houver desconto (e for menor que price)
      const promoBase = (p.promo_price != null && Number(p.promo_price) > 0 &&
                         Number(p.promo_price) < price) ? Number(p.promo_price) : null;
      if (variants) {
        variants.forEach(v => {
          const qty = Number(v.quantity || 0);
          const hasStock = qty > 0;
          if (stockMode === "with" && !hasStock) return;
          if (stockMode === "without" && hasStock) return;
          const vCost = Number(v.cost ?? cost);
          const vPrice = Number(v.price ?? price);
          // Variação pode ter promo_price próprio; senão herda do pai.
          const vPromo = (v.promo_price != null && Number(v.promo_price) > 0 &&
                          Number(v.promo_price) < vPrice) ? Number(v.promo_price) : promoBase;
          const vSold = vPromo != null ? vPromo : vPrice;
          const hasDisc = vPromo != null && vPromo < vPrice;
          rows.push({
            quantity: qty,
            name: `${p.name}${v.name ? " " + v.name : ""}`,
            category: cat,
            supplier: sup,
            unit_cost: vCost,
            unit_price: vPrice,
            unit_sold_price: vSold,
            has_discount: hasDisc,
            unit_profit: vSold - vCost,
            total_cost: hasStock ? qty * vCost : 0,
            total_value: hasStock ? qty * vSold : 0,
            total_profit: hasStock ? qty * (vSold - vCost) : 0,
            has_stock: hasStock,
          });
        });
      } else {
        const qty = Number(p.stock || 0);
        const hasStock = qty > 0;
        if (stockMode === "with" && !hasStock) return;
        if (stockMode === "without" && hasStock) return;
        const sold = promoBase != null ? promoBase : price;
        const hasDisc = promoBase != null && promoBase < price;
        rows.push({
          quantity: qty, name: p.name, category: cat, supplier: sup,
          unit_cost: cost, unit_price: price,
          unit_sold_price: sold, has_discount: hasDisc,
          unit_profit: sold - cost,
          total_cost: hasStock ? qty * cost : 0,
          total_value: hasStock ? qty * sold : 0,
          total_profit: hasStock ? qty * (sold - cost) : 0,
          has_stock: hasStock,
        });
      }
    });
    if (!rows.length) {
      closeOnError();
      return toast.error("Nenhum produto encontrado nesses filtros");
    }
    // Totals — APENAS itens com estoque
    const totalCost = rows.filter(r => r.has_stock).reduce((s, r) => s + r.total_cost, 0);
    const totalValue = rows.filter(r => r.has_stock).reduce((s, r) => s + r.total_value, 0);
    const totalProfit = totalValue - totalCost;
    const subtitle = `${stockMode === "with" ? "Com estoque" : "Sem estoque"}${allCats ? " · Todas categorias" : ` · ${selCats.length} categoria(s)`}`;

    if (format === "xlsx") {
      const rowsForSheet = rows.map(r => {
        const o = {
          "Qtd": r.quantity, "Produto": r.name, "Categoria": r.category,
        };
        if (showSupplier) o["Fornecedor"] = r.supplier || "—";
        if (showCosts) { o["Custo unit."] = r.unit_cost; o["Custo total"] = r.total_cost; }
        o["Valor unit."] = r.unit_price;
        o["Valor un. vendido"] = r.unit_sold_price;
        o["Valor total"] = r.total_value;
        if (showProfit) { o["Lucro unit."] = r.unit_profit; o["Lucro total"] = r.total_profit; }
        return o;
      });
      // Resumo no final
      rowsForSheet.push({ Qtd: "", Produto: "", Categoria: "" });
      rowsForSheet.push({ Qtd: "TOTAIS (com estoque)", Produto: "", Categoria: "",
        "Custo total": totalCost, "Valor total": totalValue, "Lucro total": totalProfit });
      exportExcel({ filename: "relatorio-produtos", sheets: [{ name: "Estoque", data: rowsForSheet }]});
      toast.success("Excel gerado");
      onClose();
      return;
    }
    // PDF
    const header = `<tr>
      <th class="r">Qtd</th><th>Produto</th><th>Categoria</th>
      ${showSupplier ? "<th>Fornecedor</th>" : ""}
      ${showCosts ? "<th class=\"r\">Custo un.</th>" : ""}
      <th class="r">Valor un.</th>
      <th class="r">Valor un. vendido</th>
      ${showProfit ? "<th class=\"r\">Lucro un.</th>" : ""}
      ${showCosts ? "<th class=\"r\">Custo total</th>" : ""}
      <th class="r">Valor total</th>
      ${showProfit ? "<th class=\"r\">Lucro total</th>" : ""}
    </tr>`;
    const body = rows.map(r => `<tr>
      <td class="r">${r.quantity}</td>
      <td>${r.name}</td>
      <td>${r.category}</td>
      ${showSupplier ? `<td>${r.supplier || "—"}</td>` : ""}
      ${showCosts ? `<td class="r">${brl(r.unit_cost)}</td>` : ""}
      <td class="r">${r.has_discount ? `<s style="text-decoration-thickness:1px;color:#888">${brl(r.unit_price)}</s>` : brl(r.unit_price)}</td>
      <td class="r"><b>${brl(r.unit_sold_price)}</b></td>
      ${showProfit ? `<td class="r">${brl(r.unit_profit)}</td>` : ""}
      ${showCosts ? `<td class="r">${brl(r.total_cost)}</td>` : ""}
      <td class="r">${brl(r.total_value)}</td>
      ${showProfit ? `<td class="r">${brl(r.total_profit)}</td>` : ""}
    </tr>`).join("");
    const totals = `
      <div class="totals">
        ${showCosts ? `<div class="row"><span>Custo total do estoque</span><span>${brl(totalCost)}</span></div>` : ""}
        <div class="row"><span>Valor total do estoque</span><span>${brl(totalValue)}</span></div>
        ${showProfit ? `<div class="row b"><span>Lucro previsto total</span><span>${brl(totalProfit)}</span></div>` : ""}
        <div class="muted" style="margin-top:6px">Os totais consideram apenas itens com estoque (qtd > 0). "Valor total" usa o valor vendido (com desconto, quando aplicável).</div>
      </div>`;
    const bodyHtml = `
      <div class="muted">${rows.length} linha(s) · ${rows.filter(r=>r.has_stock).length} com estoque</div>
      <table><thead>${header}</thead><tbody>${body}</tbody></table>
      ${totals}`;
    exportPDF({ filename: "relatorio-produtos", title: "Relatório de produtos", subtitle, store, bodyHtml, popupWindow: popup });
    onClose();
  };

  const toggleCat = (id) => setSelCats(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="products-export-dialog">
        <DialogHeader><DialogTitle>Exportar relatório · Produtos</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">Formato</Label>
            <FormatPicker value={format} onChange={setFormat}/>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Tipo de estoque</Label>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={stockMode==="with"} onChange={()=>setStockMode("with")} data-testid="stock-with"/>
                Com estoque
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={stockMode==="without"} onChange={()=>setStockMode("without")} data-testid="stock-without"/>
                Sem estoque
              </label>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Categorias</Label>
            <label className="flex items-center gap-2 text-xs cursor-pointer mb-2">
              <input type="checkbox" checked={allCats} onChange={(e)=>{setAllCats(e.target.checked); if(e.target.checked) setSelCats([]);}} data-testid="all-cats"/>
              Todas categorias
            </label>
            {!allCats && (
              <CategoriesChips categories={categories} selected={selCats}
                onToggle={toggleCat}
                onSelectAll={()=>setSelCats((categories||[]).map(c=>c.id))}
                onClear={()=>setSelCats([])}/>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={showSupplier} onChange={e=>setShowSupplier(e.target.checked)} data-testid="show-supplier"/>
              Mostrar fornecedor
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={showCosts} onChange={e=>setShowCosts(e.target.checked)} data-testid="show-costs"/>
              Mostrar custos
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={showProfit} onChange={e=>setShowProfit(e.target.checked)} data-testid="show-profit"/>
              Mostrar lucro previsto
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={generate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-btn">
            <Download size={14} className="mr-1"/> Gerar relatório
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 2) RELATÓRIO FINANCEIRO
// ═════════════════════════════════════════════════════════════════════════════
const FIN_QUICK = [
  { v: "month", label: "Este mês" },
  { v: "lastmonth", label: "Mês passado" },
  { v: "week", label: "Esta semana" },
  { v: "d15", label: "Últimos 15 dias" },
];
function computeRange(preset) {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  if (preset === "month") {
    // "Este mês" = 1º dia até o ÚLTIMO dia do mês corrente. Importante pra
    // despesas previstas (queremos ver TODAS do mês, não só até hoje).
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === "lastmonth") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === "week") {
    // Semana corrente: domingo até sábado (inclusive os futuros, pra cobrir
    // despesas que vencem ainda na semana).
    const dow = now.getDay();
    const start = new Date(now); start.setDate(now.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === "d15") {
    // Últimos 15 dias contados a partir de hoje (15 retroativos).
    const start = new Date(now); start.setDate(now.getDate() - 14);
    return { start: fmt(start), end: fmt(now) };
  }
  return { start: "", end: "" };
}

export function FinanceiroExportDialog({ open, onClose, categories }) {
  const [format, setFormat] = useState("pdf");
  const [typeSales, setTypeSales] = useState(true);
  const [typePaid, setTypePaid] = useState(true);
  const [typeServices, setTypeServices] = useState(true);
  const [allCats, setAllCats] = useState(true);
  const [selCats, setSelCats] = useState([]);
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [subtractExp, setSubtractExp] = useState(false);
  const [expMode, setExpMode] = useState("paid"); // paid|projected
  // Período independente das DESPESAS (não está atrelado ao período do
  // relatório financeiro) — usado quando 'Subtrair despesas' está ativo.
  const [expPreset, setExpPreset] = useState("month");
  const [expStart, setExpStart] = useState("");
  const [expEnd, setExpEnd] = useState("");
  // Filtro Lead — array (multi-seleção); vazio = Todos
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (!open) return;
    setFormat("pdf"); setTypeSales(true); setTypePaid(true); setTypeServices(true);
    setAllCats(true); setSelCats([]);
    setPreset("month");
    const r = computeRange("month");
    setStart(r.start); setEnd(r.end);
    setSubtractExp(false); setExpMode("paid");
    setExpPreset("month");
    setExpStart(r.start); setExpEnd(r.end);
    setSelectedLeads([]);
    api.get("/sale-leads").then(r => setLeads(r.data || [])).catch(() => {});
  }, [open]);

  const applyPreset = (p) => {
    setPreset(p);
    const r = computeRange(p);
    setStart(r.start); setEnd(r.end);
  };
  const applyExpPreset = (p) => {
    setExpPreset(p);
    const r = computeRange(p);
    setExpStart(r.start); setExpEnd(r.end);
  };

  const generate = async () => {
    if (!typeSales && !typePaid && !typeServices) return toast.error("Selecione ao menos um tipo");
    if (!start || !end) return toast.error("Defina o período");
    if (subtractExp && (!expStart || !expEnd)) return toast.error("Defina o período das despesas");
    // popup SÍNCRONO (antes de qualquer await) para PDF
    const popup = format === "pdf" ? openLoaderPopup() : null;
    if (format === "pdf" && !popup) {
      toast.error("Permita popups para exportar o PDF.");
      return;
    }
    const closeOnError = () => { try { popup && popup.close(); } catch {} };
    const store = await getStoreForHeader();
    // Backend usa ISO completo p/ TZ-aware
    const localStart = new Date(start + "T00:00:00").toISOString();
    const localEnd = new Date(end + "T23:59:59").toISOString();
    const params = { start: localStart, end: localEnd };
    if (selectedLeads.length > 0) params.lead_ids = selectedLeads;
    const finReq = api.get("/financial/summary", { params,
      paramsSerializer: { indexes: null } });
    const [finR, expR] = await Promise.all([
      finReq,
      subtractExp ? api.get("/expenses", {
        params: { start: expStart, end: expEnd }
      }).catch(()=>({data:[]})) : Promise.resolve({ data: [] })
    ]);
    const fin = finR.data || {};
    let lines = [];
    if (typeSales) (fin.sales || []).forEach(o => {
      lines.push({
        type: "Venda",
        number: o.order_number ? String(o.order_number).padStart(6, "0") : "",
        customer: o.customer_name || "",
        lead: o.lead_name || "",
        items: (o.items || []).map(i => `${i.quantity}x ${i.name}`).filter(Boolean).join(" + "),
        item_count: (o.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0),
        date: o.created_at, payment: o.payment_method || "",
        cost: Number(o.cost || 0), total: Number(o.total || 0),
        profit: Number(o.profit || 0),
      });
    });
    if (typePaid) (fin.paid_orders || []).forEach(o => {
      lines.push({
        type: "Pedido pago",
        number: o.order_number ? String(o.order_number).padStart(6, "0") : "",
        customer: o.customer_name || "",
        lead: o.lead_name || "",
        items: (o.items || []).map(i => `${i.quantity}x ${i.name}`).filter(Boolean).join(" + "),
        item_count: (o.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0),
        date: o.created_at, payment: o.payment_method || "",
        cost: Number(o.cost || 0), total: Number(o.total || 0),
        profit: Number(o.profit || 0),
      });
    });
    if (typeServices) (fin.services || []).forEach(s => {
      const desc = s.problem || (s.items || []).map(i => i.name).filter(Boolean).join(" + ") || "Assistência";
      const productLabel = s.product_name ? `${s.product_name} — ${desc}` : desc;
      lines.push({
        type: "Assistência",
        number: s.order_number ? String(s.order_number).padStart(6, "0") : "",
        customer: s.customer_name || "",
        lead: "",
        items: productLabel,
        item_count: 1,
        date: s.created_at, payment: s.payment_method || "",
        cost: Number(s.cost || 0), total: Number(s.total || s.service_total || 0),
        profit: Number(s.profit || 0),
      });
    });
    if (!lines.length) {
      closeOnError();
      return toast.error("Nenhum lançamento no período");
    }
    // Sort by date
    lines.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    // Despesas
    let expensesTotal = 0;
    if (subtractExp) {
      (expR.data || []).forEach(e => {
        if (expMode === "paid") {
          // Considera só despesas EFETIVAMENTE pagas dentro do PERÍODO DAS
          // DESPESAS (expStart..expEnd), independente do período do
          // relatório financeiro.
          if (!e.paid_at) return;
          const pd = String(e.paid_at).slice(0, 10);
          if (pd < expStart || pd > expEnd) return;
          expensesTotal += Number(e.paid_amount || e.amount || 0);
        } else {
          // projected = todas as do período (backend já filtrou por due_date)
          expensesTotal += Number(e.amount || 0);
        }
      });
    }
    const totalSold = lines.reduce((s, l) => s + l.total, 0);
    const totalCost = lines.reduce((s, l) => s + l.cost, 0);
    const totalProfit = lines.reduce((s, l) => s + l.profit, 0);
    // Comissões pagas no período — já retornadas pelo backend em fin.commissions_paid.
    // Filtradas por commission_paid_at ∈ [start, end] e excluindo vendas canceladas.
    // Considera-se como despesa e subtrai do lucro no relatório.
    const commissionsPaidTotal = Number(fin.commissions_paid_total || 0);
    const commissionsList = fin.commissions_paid || [];
    const finalProfit = totalProfit - expensesTotal - commissionsPaidTotal;
    const totalCount = lines.length;
    // Usa item_count direto do backend (já vem por linha). Bem mais robusto
    // que regex que dependia da formatação da string.
    const itemsSold = lines.reduce((s, l) => s + Number(l.item_count || 0), 0);
    const expRangeLabel = subtractExp ? ` · Despesas ${expMode === "paid" ? "pagas" : "previstas"} (${fmtBR(expStart)} → ${fmtBR(expEnd)})` : "";
    const subtitle = `${fmtBR(start)} → ${fmtBR(end)} · ${[typeSales && "Vendas", typePaid && "Pedidos pagos", typeServices && "Assistência"].filter(Boolean).join(" + ")}${expRangeLabel}`;

    if (format === "xlsx") {
      const rows = lines.map(l => ({
        "Tipo": l.type, "Nº": l.number, "Cliente": l.customer || "",
        "Lead": l.lead || "",
        "Itens / Defeito": l.items, "Data/Hora": fmtBR(l.date),
        "Pagamento": l.payment, "Custo": l.cost, "Valor": l.total, "Lucro": l.profit,
      }));
      rows.push({ Tipo: "", Nº: "", Cliente: "", "Lead": "", "Itens / Defeito": "" });
      rows.push({ Tipo: "TOTAIS", Nº: "", Cliente: "", "Lead": "",
        "Itens / Defeito": `${totalCount} lançamento(s) · ${itemsSold} itens`,
        "Custo": totalCost, "Valor": totalSold, "Lucro": totalProfit });
      // Comissões pagas — listadas como linhas de despesa individual
      commissionsList.forEach(c => {
        rows.push({
          "Tipo": "(-) Comissão paga",
          "Nº": c.order_number ? String(c.order_number).padStart(6, "0") : "",
          "Cliente": c.customer_name || "",
          "Lead": c.created_by || "",
          "Itens / Defeito": (c.items || []).map(i => `${i.quantity}x ${i.name}`).filter(Boolean).join(" + "),
          "Data/Hora": fmtBR(c.paid_at),
          "Pagamento": "",
          "Custo": 0,
          "Valor": -Number(c.amount || 0),
          "Lucro": -Number(c.amount || 0),
        });
      });
      if (commissionsPaidTotal > 0) {
        rows.push({ Tipo: "(-) Total comissões pagas", "Valor": -commissionsPaidTotal });
      }
      if (subtractExp) {
        rows.push({ Tipo: `(-) Despesas ${expMode==="paid"?"pagas":"previstas"}`,
          "Cliente": `${fmtBR(expStart)} → ${fmtBR(expEnd)}`,
          "Valor": -expensesTotal });
        rows.push({ Tipo: "LUCRO FINAL", "Valor": finalProfit });
      } else if (commissionsPaidTotal > 0) {
        rows.push({ Tipo: "LUCRO FINAL (após comissões)", "Valor": finalProfit });
      }
      exportExcel({ filename: "relatorio-financeiro", sheets: [{ name: "Lançamentos", data: rows }]});
      toast.success("Excel gerado");
      onClose();
      return;
    }
    // PDF
    const body = lines.map(l => `<tr>
      <td><span class="muted">${l.type}</span><br/><b>${l.number || "—"}</b></td>
      <td>${l.customer ? `<b>${l.customer}</b>` : "<span class=\"muted\">—</span>"}${l.lead ? `<div style="margin-top:2px;display:inline-block;background:#f3e8ff;color:#6b21a8;padding:1px 4px;border-radius:3px;font-size:8px">Lead: ${l.lead}</div>` : ""}<div style="margin-top:2px;color:#333;font-size:10px">${l.items || ""}</div></td>
      <td>${fmtBR(l.date)}</td>
      <td>${l.payment || "—"}</td>
      <td class="r">${brl(l.cost)}</td>
      <td class="r">${brl(l.total)}</td>
      <td class="r"><b>${brl(l.profit)}</b></td>
    </tr>`).join("");
    const bodyHtml = `
      <div class="cards">
        <div class="card"><h3>Lançamentos</h3><div class="v">${totalCount}</div></div>
        <div class="card"><h3>Itens vendidos</h3><div class="v">${itemsSold}</div></div>
        <div class="card"><h3>Faturamento</h3><div class="v">${brl(totalSold)}</div></div>
        <div class="card"><h3>Lucro</h3><div class="v paid">${brl(totalProfit)}</div></div>
      </div>
      <table><thead><tr>
        <th>Tipo / Nº</th><th>Cliente / Itens</th><th>Data</th><th>Pagto</th>
        <th class="r">Custo</th><th class="r">Valor</th><th class="r">Lucro</th>
      </tr></thead><tbody>${body}</tbody></table>
      <div class="totals">
        <div class="row"><span>Total faturado</span><span>${brl(totalSold)}</span></div>
        <div class="row"><span>Total de custos</span><span>${brl(totalCost)}</span></div>
        <div class="row"><span>Lucro bruto</span><span class="paid">${brl(totalProfit)}</span></div>
        ${commissionsPaidTotal > 0 ? `
          <div class="row"><span>(-) Comissões pagas (${commissionsList.length})</span><span class="overdue">- ${brl(commissionsPaidTotal)}</span></div>
        ` : ""}
        ${subtractExp ? `
          <div class="row"><span>(-) Despesas ${expMode==="paid"?"pagas":"previstas"} (${fmtBR(expStart)} → ${fmtBR(expEnd)})</span><span class="overdue">- ${brl(expensesTotal)}</span></div>
          <div class="row b"><span>Lucro final</span><span class="${finalProfit>=0?"paid":"overdue"}">${brl(finalProfit)}</span></div>
        ` : (commissionsPaidTotal > 0 ? `<div class="row b"><span>Lucro final</span><span class="${finalProfit>=0?"paid":"overdue"}">${brl(finalProfit)}</span></div>` : `<div class="row b"><span>Lucro</span><span class="paid">${brl(totalProfit)}</span></div>`)}
      </div>`;
    exportPDF({ filename: "relatorio-financeiro", title: "Relatório financeiro", subtitle, store, bodyHtml, popupWindow: popup });
    onClose();
  };

  const toggleCat = (id) => setSelCats(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="financeiro-export-dialog">
        <DialogHeader><DialogTitle>Exportar relatório · Financeiro</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">Formato</Label>
            <FormatPicker value={format} onChange={setFormat}/>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Tipos</Label>
            <div className="space-y-1 text-xs">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={typeSales} onChange={e=>setTypeSales(e.target.checked)} data-testid="fin-type-sales"/> Vendas (PDV)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={typePaid} onChange={e=>setTypePaid(e.target.checked)} data-testid="fin-type-paid"/> Pedidos pagos</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={typeServices} onChange={e=>setTypeServices(e.target.checked)} data-testid="fin-type-services"/> Assistência</label>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Categorias de produtos</Label>
            <label className="flex items-center gap-2 text-xs cursor-pointer mb-2">
              <input type="checkbox" checked={allCats} onChange={(e)=>{setAllCats(e.target.checked); if(e.target.checked) setSelCats([]);}} data-testid="fin-all-cats"/>
              Todas categorias
            </label>
            {!allCats && (
              <CategoriesChips categories={categories} selected={selCats}
                onToggle={toggleCat}
                onSelectAll={()=>setSelCats((categories||[]).map(c=>c.id))}
                onClear={()=>setSelCats([])}/>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">
              Lead (origem do cliente)
              {selectedLeads.length > 0 && (
                <span className="ml-2 text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded-full">
                  {selectedLeads.length} selecionado{selectedLeads.length !== 1 ? "s" : ""}
                </span>
              )}
            </Label>
            {leads.length === 0 ? (
              <p className="text-xs text-neutral-400 italic">Nenhum Lead cadastrado.</p>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <button type="button" onClick={()=>setSelectedLeads([])}
                    className="text-neutral-500 hover:underline"
                    data-testid="fin-export-leads-clear">
                    Limpar (Todos)
                  </button>
                  <span className="text-neutral-400 ml-auto">{selectedLeads.length}/{leads.length}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="fin-export-leads-chips">
                  {leads.map(l => {
                    const on = selectedLeads.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setSelectedLeads(prev => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id])}
                        data-testid={`fin-export-lead-chip-${l.id}`}
                        className={`text-[11px] px-2 py-1 rounded-full border transition ${on
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white dark:bg-zinc-900 text-neutral-700 dark:text-neutral-300 border-[#E5E5E5] dark:border-white/10 hover:border-purple-400"
                          }`}
                      >
                        {l.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-neutral-400 italic mt-1.5">
                  {selectedLeads.length === 0 ? "Nenhum selecionado = mostrar Todos" : "Apenas os Leads selecionados serão exportados"}
                </p>
              </>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Período</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {FIN_QUICK.map(q => (
                <button key={q.v} type="button" onClick={()=>applyPreset(q.v)}
                  className={`text-[11px] px-2 py-1 rounded border ${preset===q.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                  data-testid={`fin-preset-${q.v}`}>
                  {q.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">De</Label><Input type="date" value={start} onChange={e=>{setStart(e.target.value);setPreset("custom");}} data-testid="fin-start"/></div>
              <div><Label className="text-[10px]">Até</Label><Input type="date" value={end} onChange={e=>{setEnd(e.target.value);setPreset("custom");}} data-testid="fin-end"/></div>
            </div>
          </div>
          <div className="border border-[#E5E5E5] dark:border-white/10 rounded-md p-2 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={subtractExp} onChange={e=>setSubtractExp(e.target.checked)} data-testid="fin-subtract-exp"/>
              Subtrair despesas do lucro
            </label>
            {subtractExp && (
              <div className="ml-5 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={expMode==="paid"} onChange={()=>setExpMode("paid")} data-testid="fin-exp-paid"/> Apenas pagas</label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={expMode==="projected"} onChange={()=>setExpMode("projected")} data-testid="fin-exp-projected"/> Todas previstas</label>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500 mb-1">Período das despesas (independente do período do relatório acima)</div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {FIN_QUICK.map(q => (
                      <button key={q.v} type="button" onClick={()=>applyExpPreset(q.v)}
                        className={`text-[11px] px-2 py-1 rounded border ${expPreset===q.v ? "bg-emerald-600 text-white border-emerald-600" : "border-[#E5E5E5]"}`}
                        data-testid={`fin-exp-preset-${q.v}`}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">De</Label><Input type="date" value={expStart} onChange={e=>{setExpStart(e.target.value);setExpPreset("custom");}} data-testid="fin-exp-start"/></div>
                    <div><Label className="text-[10px]">Até</Label><Input type="date" value={expEnd} onChange={e=>{setExpEnd(e.target.value);setExpPreset("custom");}} data-testid="fin-exp-end"/></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={generate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-btn">
            <Download size={14} className="mr-1"/> Gerar relatório
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 3) RELATÓRIO DE DESPESAS
// ═════════════════════════════════════════════════════════════════════════════
export function DespesasExportDialog({ open, onClose }) {
  const [format, setFormat] = useState("pdf");
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [mode, setMode] = useState("projected"); // paid|projected

  useEffect(() => {
    if (!open) return;
    setFormat("pdf"); setPreset("month");
    const r = computeRange("month");
    setStart(r.start); setEnd(r.end);
    setMode("projected");
  }, [open]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      const r = computeRange(p);
      setStart(r.start); setEnd(r.end);
    }
  };

  const generate = async () => {
    if (!start || !end) return toast.error("Defina o período");
    // popup SÍNCRONO (antes de qualquer await)
    const popup = format === "pdf" ? openLoaderPopup() : null;
    if (format === "pdf" && !popup) {
      toast.error("Permita popups para exportar o PDF.");
      return;
    }
    const closeOnError = () => { try { popup && popup.close(); } catch {} };
    const store = await getStoreForHeader();
    const r = await api.get("/expenses", { params: { start, end } });
    let items = r.data || [];
    if (mode === "paid") {
      // Apenas despesas EFETIVAMENTE pagas dentro do período do relatório
      // (paid_at ∈ [start, end]). O backend já filtrou por due_date.
      items = items.filter(e => {
        if (!e.paid_at) return false;
        const pd = String(e.paid_at).slice(0, 10);
        return pd >= start && pd <= end;
      });
    }
    if (!items.length) {
      closeOnError();
      return toast.error("Nenhuma despesa no período");
    }
    items.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    const total = items.reduce((s, e) => {
      // Em modo 'paid' usa o valor pago; em 'projected' usa o valor previsto
      if (mode === "paid") return s + Number(e.paid_amount || e.amount || 0);
      return s + Number(e.amount || 0);
    }, 0);
    const totalPaid = items.filter(e => e.paid_at).reduce((s, e) => s + Number(e.paid_amount || e.amount || 0), 0);
    const totalOverdue = items.filter(e => e.status === "overdue").reduce((s, e) => s + Number(e.amount || 0), 0);
    const totalPending = items.filter(e => e.status === "pending").reduce((s, e) => s + Number(e.amount || 0), 0);
    const subtitle = `${fmtBR(start)} → ${fmtBR(end)} · ${mode === "paid" ? "Apenas pagas" : "Todas previstas"}`;

    if (format === "xlsx") {
      const rows = items.map(e => ({
        "Despesa": e.name, "Categoria": e.category_name || "—",
        "Tipo": e.type === "fixed" ? "Fixa" : "Avulsa",
        "Vencimento": fmtBR(e.due_date), "Status": e.status === "paid" ? "Paga" : e.status === "overdue" ? "Atrasada" : "Pendente",
        "Pago em": e.paid_at ? fmtBR(e.paid_at) : "",
        "Forma de pgto": e.payment_method || "", "Valor": Number(e.amount || 0),
        "Pago": Number(e.paid_amount || 0), "Obs": e.notes || "",
      }));
      rows.push({ Despesa: "" });
      rows.push({ Despesa: "TOTAL", "Valor": total, "Pago": totalPaid });
      exportExcel({ filename: "relatorio-despesas", sheets: [{ name: "Despesas", data: rows }]});
      toast.success("Excel gerado");
      onClose();
      return;
    }
    const body = items.map(e => `<tr>
      <td><b>${e.name}</b><div class="muted">${e.notes || ""}</div></td>
      <td>${e.category_name || "—"}</td>
      <td>${e.type === "fixed" ? "Fixa" : "Avulsa"}</td>
      <td>${fmtBR(e.due_date)}</td>
      <td class="${e.status}">${e.status === "paid" ? "Paga" : e.status === "overdue" ? "Atrasada" : "Pendente"}${e.paid_at ? `<div class="muted">${fmtBR(e.paid_at)}</div>` : ""}</td>
      <td class="r"><b>${brl(e.amount)}</b></td>
    </tr>`).join("");
    const bodyHtml = `
      <div class="cards">
        <div class="card"><h3>Lançamentos</h3><div class="v">${items.length}</div></div>
        <div class="card"><h3>Total</h3><div class="v">${brl(total)}</div></div>
        <div class="card"><h3>Pago</h3><div class="v paid">${brl(totalPaid)}</div></div>
        <div class="card"><h3>Atrasada</h3><div class="v overdue">${brl(totalOverdue)}</div></div>
      </div>
      <table><thead><tr>
        <th>Despesa</th><th>Categoria</th><th>Tipo</th><th>Vencimento</th><th>Status</th><th class="r">Valor</th>
      </tr></thead><tbody>${body}</tbody></table>
      <div class="totals">
        <div class="row"><span>Total de despesas</span><span>${brl(total)}</span></div>
        <div class="row"><span>Total pago</span><span class="paid">${brl(totalPaid)}</span></div>
        <div class="row"><span>Total pendente</span><span class="pending">${brl(totalPending)}</span></div>
        <div class="row b"><span>Total atrasado</span><span class="overdue">${brl(totalOverdue)}</span></div>
      </div>`;
    exportPDF({ filename: "relatorio-despesas", title: "Relatório de despesas", subtitle, store, bodyHtml, popupWindow: popup });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="despesas-export-dialog">
        <DialogHeader><DialogTitle>Exportar relatório · Despesas</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">Formato</Label>
            <FormatPicker value={format} onChange={setFormat}/>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Período</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {FIN_QUICK.map(q => (
                <button key={q.v} type="button" onClick={()=>applyPreset(q.v)}
                  className={`text-[11px] px-2 py-1 rounded border ${preset===q.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                  data-testid={`desp-preset-${q.v}`}>
                  {q.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">De</Label><Input type="date" value={start} onChange={e=>{setStart(e.target.value);setPreset("custom");}} data-testid="desp-start"/></div>
              <div><Label className="text-[10px]">Até</Label><Input type="date" value={end} onChange={e=>{setEnd(e.target.value);setPreset("custom");}} data-testid="desp-end"/></div>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Modo</Label>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={mode==="paid"} onChange={()=>setMode("paid")} data-testid="desp-mode-paid"/> Apenas despesas pagas</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={mode==="projected"} onChange={()=>setMode("projected")} data-testid="desp-mode-projected"/> Todas previstas</label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={generate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-btn">
            <Download size={14} className="mr-1"/> Gerar relatório
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
